/**
 * Opportunity Summary Dashboard
 * Enhanced overview with program health, metrics, and visual indicators
 * Designed for automotive supplier business development
 */
import React, { useMemo } from 'react';
import { 
  TrendingUp, TrendingDown, DollarSign, Package, Calendar, 
  AlertTriangle, CheckCircle, Clock, Target, Zap, BarChart3,
  ShieldAlert, ShieldCheck, Activity, Users
} from 'lucide-react';
import { formatCompactCurrency } from '../../api/pipeline';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

// Stage configuration with order for progress calculation
const STAGE_ORDER = ['target', 'qualified', 'rfq', 'quoted', 'awarded', 'sourced'];
const STAGE_CONFIG = {
  target: { label: 'Target', color: 'bg-blue-500', order: 0 },
  qualified: { label: 'Qualified', color: 'bg-purple-500', order: 1 },
  rfq: { label: 'RFQ', color: 'bg-indigo-500', order: 2 },
  quoted: { label: 'Quoted', color: 'bg-cyan-500', order: 3 },
  awarded: { label: 'Awarded', color: 'bg-emerald-500', order: 4 },
  sourced: { label: 'Sourced', color: 'bg-green-500', order: 5 },
  lost: { label: 'Lost', color: 'bg-slate-400', order: -1 },
};

// Metric Card Component
const MetricCard = ({ icon: Icon, label, value, subValue, trend, color = 'blue', size = 'default' }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 border-blue-100',
    green: 'bg-emerald-50 text-emerald-600 border-emerald-100',
    amber: 'bg-amber-50 text-amber-600 border-amber-100',
    red: 'bg-red-50 text-red-600 border-red-100',
    purple: 'bg-purple-50 text-purple-600 border-purple-100',
    slate: 'bg-slate-50 text-slate-600 border-slate-100',
  };

  return (
    <div className={`rounded-lg border p-4 ${colorClasses[color]} ${size === 'large' ? 'col-span-2' : ''}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 opacity-70" />
          <span className="text-xs font-medium uppercase tracking-wide opacity-70">{label}</span>
        </div>
        {trend && (
          <span className={`text-xs font-medium flex items-center gap-0.5 ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
            {trend > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {Math.abs(trend)}%
          </span>
        )}
      </div>
      <p className={`font-bold mt-2 ${size === 'large' ? 'text-2xl' : 'text-xl'}`}>{value}</p>
      {subValue && <p className="text-xs mt-1 opacity-70">{subValue}</p>}
    </div>
  );
};

// Risk Indicator Component
const RiskIndicator = ({ label, value, maxValue = 10 }) => {
  const getRiskLevel = (val) => {
    if (val === null || val === undefined) return { level: 'unknown', color: 'slate', text: 'No Data' };
    if (val <= 3) return { level: 'low', color: 'green', text: 'Low Risk' };
    if (val <= 6) return { level: 'medium', color: 'amber', text: 'Medium Risk' };
    return { level: 'high', color: 'red', text: 'High Risk' };
  };

  const risk = getRiskLevel(value);
  const percentage = value !== null && value !== undefined ? (value / maxValue) * 100 : 0;

  const colorClasses = {
    green: 'bg-green-500',
    amber: 'bg-amber-500',
    red: 'bg-red-500',
    slate: 'bg-slate-300',
  };

  return (
    <div className="flex-1">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-slate-600">{label}</span>
        <span className={`text-xs font-medium ${risk.color === 'green' ? 'text-green-600' : risk.color === 'amber' ? 'text-amber-600' : risk.color === 'red' ? 'text-red-600' : 'text-slate-500'}`}>
          {risk.text}
        </span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div 
          className={`h-full rounded-full transition-all ${colorClasses[risk.color]}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      {value !== null && value !== undefined && (
        <p className="text-xs text-slate-500 mt-0.5">{value}/10</p>
      )}
    </div>
  );
};

const OpportunitySummaryDashboard = ({ opportunity }) => {
  // Calculate derived metrics
  const metrics = useMemo(() => {
    if (!opportunity) return null;

    const now = new Date();
    
    // Days to SOP
    let daysToSOP = null;
    if (opportunity.sop_date) {
      const sopDate = new Date(opportunity.sop_date);
      daysToSOP = Math.ceil((sopDate - now) / (1000 * 60 * 60 * 24));
    }

    // Days in current stage (from last_activity_at or created_at)
    let daysInStage = null;
    const lastActivity = opportunity.last_activity_at ? new Date(opportunity.last_activity_at) : null;
    const createdAt = opportunity.created_at ? new Date(opportunity.created_at) : null;
    const referenceDate = lastActivity || createdAt;
    if (referenceDate) {
      daysInStage = Math.ceil((now - referenceDate) / (1000 * 60 * 60 * 24));
    }

    // Weighted revenue
    const confidence = opportunity.confidence_percent || 0;
    const revenue = opportunity.revenue_potential_cents || 0;
    const weightedRevenue = Math.round((revenue * confidence) / 100);

    // Stage progress percentage
    const currentStage = STAGE_CONFIG[opportunity.stage];
    const stageProgress = opportunity.stage === 'lost' 
      ? 0 
      : ((currentStage?.order || 0) / (STAGE_ORDER.length - 1)) * 100;

    // Annual volume (from monthly forecast or total lifetime volume)
    let annualVolume = null;
    if (opportunity.monthly_volume_forecast) {
      const currentYear = new Date().getFullYear();
      const yearData = opportunity.monthly_volume_forecast[currentYear.toString()];
      if (yearData && typeof yearData === 'object') {
        annualVolume = Object.values(yearData).reduce((sum, v) => sum + (v || 0), 0);
      }
    } else if (opportunity.total_lifetime_volume && opportunity.sop_date && opportunity.eop_date) {
      const sopYear = new Date(opportunity.sop_date).getFullYear();
      const eopYear = new Date(opportunity.eop_date).getFullYear();
      const years = Math.max(1, eopYear - sopYear + 1);
      annualVolume = Math.round(opportunity.total_lifetime_volume / years);
    }

    // Program health score (0-100)
    // Based on: confidence (40%), IHS risk (30%), stage progress (30%)
    let healthScore = 0;
    let healthFactors = [];
    
    // Confidence contributes 40%
    healthScore += confidence * 0.4;
    healthFactors.push({ label: 'Confidence', value: confidence, weight: 40 });
    
    // IHS Risk contributes 30% (inverted - lower risk = higher score)
    const ihsRisk = opportunity.ihs_short_term_risk || opportunity.ihs_long_term_risk;
    if (ihsRisk !== null && ihsRisk !== undefined) {
      const riskScore = Math.max(0, 100 - (ihsRisk * 10));
      healthScore += riskScore * 0.3;
      healthFactors.push({ label: 'Market Risk', value: riskScore, weight: 30 });
    } else {
      // If no IHS data, use 50% as neutral
      healthScore += 50 * 0.3;
      healthFactors.push({ label: 'Market Risk', value: 50, weight: 30, note: 'No IHS data' });
    }
    
    // Stage progress contributes 30%
    healthScore += stageProgress * 0.3;
    healthFactors.push({ label: 'Stage Progress', value: stageProgress, weight: 30 });

    return {
      daysToSOP,
      daysInStage,
      weightedRevenue,
      stageProgress,
      annualVolume,
      healthScore: Math.round(healthScore),
      healthFactors,
      confidence,
      revenue,
    };
  }, [opportunity]);

  if (!opportunity || !metrics) return null;

  const getHealthColor = (score) => {
    if (score >= 70) return 'green';
    if (score >= 40) return 'amber';
    return 'red';
  };

  const healthColor = getHealthColor(metrics.healthScore);

  // Get health icon based on score
  const renderHealthIcon = () => {
    const iconClass = `w-5 h-5 ${
      healthColor === 'green' ? 'text-emerald-600' :
      healthColor === 'amber' ? 'text-amber-600' : 'text-red-600'
    }`;
    
    if (metrics.healthScore >= 70) return <ShieldCheck className={iconClass} />;
    if (metrics.healthScore >= 40) return <ShieldAlert className={iconClass} />;
    return <AlertTriangle className={iconClass} />;
  };

  return (
    <div className="space-y-6">
      {/* Program Health Score - Hero Section */}
      <div className={`rounded-xl p-5 ${
        healthColor === 'green' ? 'bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-200' :
        healthColor === 'amber' ? 'bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200' :
        'bg-gradient-to-br from-red-50 to-rose-50 border border-red-200'
      }`}>
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              {renderHealthIcon()}
              <h3 className="text-sm font-semibold text-slate-700">Program Health Score</h3>
            </div>
            <div className="flex items-baseline gap-2">
              <span className={`text-4xl font-bold ${
                healthColor === 'green' ? 'text-emerald-700' :
                healthColor === 'amber' ? 'text-amber-700' : 'text-red-700'
              }`}>
                {metrics.healthScore}
              </span>
              <span className="text-lg text-slate-500">/100</span>
            </div>
            <p className={`text-sm mt-1 ${
              healthColor === 'green' ? 'text-emerald-600' :
              healthColor === 'amber' ? 'text-amber-600' : 'text-red-600'
            }`}>
              {metrics.healthScore >= 70 ? 'Strong Position' :
               metrics.healthScore >= 40 ? 'Moderate Risk' : 'Needs Attention'}
            </p>
          </div>
          
          {/* Health Factors Breakdown */}
          <div className="text-right text-xs text-slate-500 space-y-1">
            {metrics.healthFactors.map((factor, idx) => (
              <div key={idx} className="flex items-center justify-end gap-2">
                <span>{factor.label}</span>
                <span className="font-medium text-slate-700">{Math.round(factor.value)}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Stage Progress Bar */}
      <div className="bg-white rounded-lg border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-700">Pipeline Progress</h3>
          <Badge variant="outline" className={`${STAGE_CONFIG[opportunity.stage]?.color} text-white border-0`}>
            {STAGE_CONFIG[opportunity.stage]?.label || opportunity.stage}
          </Badge>
        </div>
        
        {/* Visual Stage Progress */}
        <div className="relative">
          <div className="flex justify-between mb-2">
            {STAGE_ORDER.map((stage, idx) => {
              const config = STAGE_CONFIG[stage];
              const isActive = opportunity.stage === stage;
              const isPast = config.order < (STAGE_CONFIG[opportunity.stage]?.order || 0);
              
              return (
                <TooltipProvider key={stage}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex flex-col items-center cursor-pointer">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                          isActive ? `${config.color} text-white ring-4 ring-offset-2 ${config.color.replace('bg-', 'ring-')}/30` :
                          isPast ? `${config.color} text-white` : 'bg-slate-200 text-slate-500'
                        }`}>
                          {idx + 1}
                        </div>
                        <span className={`text-xs mt-1 ${isActive ? 'font-semibold text-slate-900' : 'text-slate-500'}`}>
                          {config.label}
                        </span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{config.label} Stage</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              );
            })}
          </div>
          
          {/* Progress Line */}
          <div className="absolute top-4 left-4 right-4 h-0.5 bg-slate-200 -z-10">
            <div 
              className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-emerald-500 transition-all"
              style={{ width: `${metrics.stageProgress}%` }}
            />
          </div>
        </div>
        
        {opportunity.stage === 'lost' && (
          <div className="mt-3 p-2 bg-slate-100 rounded text-sm text-slate-600 text-center">
            This opportunity was marked as Lost
          </div>
        )}
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricCard
          icon={DollarSign}
          label="Total Revenue"
          value={formatCompactCurrency(metrics.revenue)}
          subValue={`Weighted: ${formatCompactCurrency(metrics.weightedRevenue)}`}
          color="green"
        />
        <MetricCard
          icon={Target}
          label="Confidence"
          value={`${metrics.confidence}%`}
          subValue="Win probability"
          color={metrics.confidence >= 70 ? 'green' : metrics.confidence >= 40 ? 'amber' : 'red'}
        />
        <MetricCard
          icon={Calendar}
          label="Days to SOP"
          value={metrics.daysToSOP !== null ? (metrics.daysToSOP > 0 ? metrics.daysToSOP : 'Past Due') : 'Not Set'}
          subValue={opportunity.sop_date ? new Date(opportunity.sop_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : null}
          color={metrics.daysToSOP !== null && metrics.daysToSOP < 90 ? 'amber' : 'blue'}
        />
        <MetricCard
          icon={Package}
          label="Linked Parts"
          value={opportunity.linked_parts_count || 0}
          subValue={metrics.annualVolume ? `${(metrics.annualVolume / 1000).toFixed(0)}K annual vol` : null}
          color="purple"
        />
      </div>

      {/* IHS Risk Indicators */}
      {(opportunity.ihs_short_term_risk !== null || opportunity.ihs_long_term_risk !== null || opportunity.ihs_program_identifier) && (
        <div className="bg-slate-50 rounded-lg border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-4 h-4 text-slate-500" />
            <h3 className="text-sm font-semibold text-slate-700">IHS Market Intelligence</h3>
            {opportunity.ihs_program_identifier && (
              <Badge variant="outline" className="text-xs ml-auto">
                {opportunity.ihs_program_identifier}
              </Badge>
            )}
          </div>
          <div className="flex gap-6">
            <RiskIndicator 
              label="Short-Term Risk" 
              value={opportunity.ihs_short_term_risk} 
            />
            <RiskIndicator 
              label="Long-Term Risk" 
              value={opportunity.ihs_long_term_risk} 
            />
          </div>
        </div>
      )}

      {/* Volume Forecast Preview (if available) */}
      {opportunity.monthly_volume_forecast && Object.keys(opportunity.monthly_volume_forecast).length > 0 && (
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Activity className="w-4 h-4 text-slate-500" />
            <h3 className="text-sm font-semibold text-slate-700">Volume Forecast</h3>
          </div>
          <div className="flex gap-2">
            {Object.entries(opportunity.monthly_volume_forecast).slice(0, 5).map(([year, monthData]) => {
              const yearTotal = typeof monthData === 'object' 
                ? Object.values(monthData).reduce((sum, v) => sum + (v || 0), 0)
                : monthData;
              return (
                <div key={year} className="flex-1 text-center p-2 bg-slate-50 rounded">
                  <p className="text-lg font-bold text-slate-900">{(yearTotal / 1000).toFixed(0)}K</p>
                  <p className="text-xs text-slate-500">{year}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Quick Info Row */}
      <div className="flex flex-wrap gap-2 text-xs">
        {opportunity.region && (
          <Badge variant="secondary" className="gap-1">
            <span className="opacity-60">Region:</span> {opportunity.region}
          </Badge>
        )}
        {opportunity.country && (
          <Badge variant="secondary" className="gap-1">
            <span className="opacity-60">Country:</span> {opportunity.country}
          </Badge>
        )}
        {opportunity.plant_location && (
          <Badge variant="secondary" className="gap-1">
            <span className="opacity-60">Plant:</span> {opportunity.plant_location}
          </Badge>
        )}
        {metrics.daysInStage !== null && (
          <Badge variant="outline" className="gap-1">
            <Clock className="w-3 h-3" />
            {metrics.daysInStage} days in stage
          </Badge>
        )}
      </div>
    </div>
  );
};

export default OpportunitySummaryDashboard;
